import React, { useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { useTranslation } from 'react-i18next'
import remarkGfm from 'remark-gfm'
import IconifyIcon from '../wrapper/IconifyIcon'
import QuantumOddsCTA from './QuantumOddsCTA'
import { OracleChatWelcome, OracleChatBanner } from './OracleChatWelcome'
import './PolymarketHub.css'
import './QuantumOddsHub.css'

const makeMessage = (role, text = '') => ({
  id: `${role}-${Date.now()}-${Math.random()}`, role, text,
})

const parseSseStream = async (stream, onEvent) => {
  const reader = stream.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  const parseBlock = (block) => {
    let type = 'message'
    const data = []
    block.split('\n').forEach((line) => {
      if (line.startsWith('event:')) type = line.slice(6).trim()
      if (line.startsWith('data:')) data.push(line.slice(5).trimStart())
    })
    if (data.length) onEvent(type, JSON.parse(data.join('\n')))
  }
  while (true) {
    const { done, value } = await reader.read()
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done }).replace(/\r\n/g, '\n')
    let boundary = buffer.indexOf('\n\n')
    while (boundary !== -1) {
      parseBlock(buffer.slice(0, boundary))
      buffer = buffer.slice(boundary + 2)
      boundary = buffer.indexOf('\n\n')
    }
    if (done) break
  }
  if (buffer.trim()) parseBlock(buffer)
}

const QuantumOddsHub = ({ authenticatedFetch, user, active = true }) => {
  const { t, i18n } = useTranslation()
  const tr = (key) => t(`oracleChat.${key}`)
  const localized = /^(en|es)/.test(i18n.resolvedLanguage || i18n.language || '')
  const [input, setInput] = useState('')
  const [messages, setMessages] = useState([])
  const [conversationId, setConversationId] = useState(null)
  const [loading, setLoading] = useState(false)
  const [activity, setActivity] = useState('')
  const [error, setError] = useState('')
  const inputRef = useRef(null)
  const chatRef = useRef(null)

  useEffect(() => {
    setConversationId(null)
    setMessages([])
  }, [user?.uid])

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight
  }, [messages, activity])

  const ask = async (rawPrompt) => {
    const prompt = String(rawPrompt || '').trim()
    if (!prompt || loading) return
    const assistant = { ...makeMessage('assistant'), streaming: true }
    setMessages((current) => [...current, makeMessage('user', prompt), assistant])
    setInput('')
    setError('')
    setLoading(true)
    setActivity(tr('quantumThinking'))
    const updateAssistant = (text) => setMessages((current) => current.map((item) => (
      item.id === assistant.id ? { ...item, text: item.text + text } : item
    )))
    try {
      const request = user ? authenticatedFetch : (path, options) => fetch(path, {
        ...options,
        headers: { 'Content-Type': 'application/json', ...(options?.headers || {}) },
      })
      const response = await request('/api/oracle/chat', {
        method: 'POST',
        body: JSON.stringify({ message: prompt, conversationId, mode: 'quantumodds' }),
      })
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}))
        throw new Error((localized ? tr('quantumFailed') : payload.message || tr('quantumFailed')))
      }
      await parseSseStream(response.body, (type, payload) => {
        if (type === 'ready') setConversationId(payload.conversationId)
        if (type === 'status') setActivity((localized ? tr('analysing') : payload.message || tr('analysing')))
        if (type === 'tool') setActivity(payload.state === 'completed' ? tr('organizing') : tr('quantumThinking'))
        if (type === 'delta') updateAssistant(payload.text || '')
        if (type === 'error') throw new Error((localized ? tr('interrupted') : payload.message || tr('interrupted')))
      })
    } catch (requestError) {
      setError(requestError.message)
      setMessages((current) => current.map((item) => (
        item.id === assistant.id ? { ...item, failed: true, text: item.text || tr('incomplete') } : item
      )))
    } finally {
      setMessages((current) => current.map((item) => item.id === assistant.id ? { ...item, streaming: false } : item))
      setLoading(false)
      setActivity('')
    }
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      ask(input)
    }
  }

  return (
    <section className={`oracle-pm-hub oracle-quantum-hub ${messages.length ? 'has-conversation' : 'is-empty'}`}>
      <section className="oracle-pm-general">
        {!messages.length && <OracleChatWelcome oracle="quantumodds" active={active} onSelect={(prompt) => { setInput(prompt); inputRef.current?.focus() }} />}
        <div ref={chatRef} className="oracle-pm-chat oracle-message-list" aria-live="polite">
          {messages.map((item) => <article key={item.id} className={`oracle-message is-${item.role}`}>
            <div className="oracle-message-avatar">{item.role === 'assistant' ? 'FM' : (user?.displayName || user?.email || 'U').charAt(0).toUpperCase()}</div>
            <div className="oracle-message-content">
              <div className="oracle-message-label">{item.role === 'assistant' ? t('oracleHome.cards.quantumodds.title') : tr('you')}</div>
              {item.text ? <div className="oracle-markdown"><ReactMarkdown remarkPlugins={[remarkGfm]}>{item.text}</ReactMarkdown>{item.role === 'assistant' && !item.streaming && !item.failed && <QuantumOddsCTA />}</div> : item.streaming && <div className="oracle-thinking"><i /><i /><i /></div>}
              {item.role === 'assistant' && item.text && !item.streaming && <button type="button" className="oracle-copy" onClick={() => navigator.clipboard?.writeText(item.text)}><IconifyIcon icon="solar:copy-linear" width="16" /> {tr('copy')}</button>}
            </div>
          </article>)}
          {loading && activity && <div className="oracle-activity"><span><i /></span><strong>{activity}</strong></div>}
        </div>
        {error && <div className="oracle-error"><IconifyIcon icon="solar:danger-triangle-linear" width="18" />{error}</div>}
        <form className="oracle-composer oracle-pm-composer" onSubmit={(event) => { event.preventDefault(); ask(input) }}>
          <textarea ref={inputRef} value={input} onChange={(event) => setInput(event.target.value.slice(0, 2000))} onKeyDown={handleKeyDown} aria-label={tr('quantumAsk')} placeholder={tr('quantumPlaceholder')} rows={messages.length ? 1 : 2} disabled={loading} />
          <div className="oracle-composer-footer">
            {messages.length > 0 && <div><IconifyIcon icon="solar:database-linear" width="15" /> {tr('quantumData')}</div>}
            <button type="submit" className={`oracle-send-button ${!messages.length ? 'oracle-send-text' : ''}`} disabled={!input.trim() || loading} aria-label={t('oracleCurrent.sendQuestion', { defaultValue: 'Enviar pergunta' })}>{messages.length ? <IconifyIcon icon="solar:arrow-up-linear" width="20" /> : tr('send')}</button>
          </div>
        </form>
        {!messages.length && <div className="oracle-pm-suggestions" aria-label={tr('quantumSuggestionsLabel') }>
          {t('oracleChat.quantumSuggestions', { returnObjects: true }).map((prompt) => <button type="button" key={prompt} onClick={() => ask(prompt)}>{prompt}</button>)}
        </div>}
        {!messages.length && <OracleChatBanner />}
      </section>
    </section>
  )
}

export default QuantumOddsHub
